import { readonly, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import {
  httpLogPlugin,
  appRunUuidStorage,
  logService,
  ScanService,
  isVoucher,
  envService,
} from '@nsftx/seven-client-retail-utils'
import {
  deviceManagementService,
  deviceManagementPrintService,
  type DmPrintConfig,
  useDeviceManagementStore,
  setDeviceAccessToken,
  getDeviceAccessToken,
} from '@nsftx/seven-client-retail-vue-dm'
import {
  bindEmitKeydown,
  bindShortcutKeys,
  configureRetailSdk,
  getCurrencySymbol,
  useSpinnerStore,
} from '@nsftx/seven-nxg-retail-vue-sdk'
import {
  cmsService,
  useNotification,
  useSevenIgamingPlatformConfig,
  useLatestBetslips,
  useTokenPayloadStore,
  type NotificationEventConfig,
  cashRegisterService,
} from '@nsftx/seven-client-retail-vue-sgp'
import {
  useDmCoreSpaceOperatorAuthStore,
  useDmCoreSpaceOperatorStore,
} from '@nsftx/seven-client-retail-vue-dm-core'
import errorParser from '@/utils/errorParser'
import { useSettingsStore } from '@/stores/settings/settingsStore'
import { useCmsConfigStore } from '@/stores/cms/cmsConfigStore'
import { useGameConfigurationStore } from '@/stores/cms/gameConfigurationStore'
import { useShortcutsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { bootGames } from '@/modules/games/gameBooting'
import printService from '@/modules/print/services/printService'
import router from '@/router'
import AppBootstrapError from '@/classes/AppBootstrapError'
import { useVoucherPOSStore } from '@/stores/vouchers/voucherPOSStore'
import { initPOSIntegrator } from '@/modules/integrator/integratorPosService'
import { checkAndShowSlipDetails } from '@/modules/slip-check/slipCheckService'
import { checkAndShowVoucherDetails } from '@/modules/voucher/voucherPOSService'
import { registerPayoutHooks } from '@/modules/slip-payout/slipPayoutService'
import { useSlipCheckStore } from '@/stores/slip-check/slipCheckStore'
import { applyTenantNumberFormat } from '@/modules/number-format/applyTenantNumberFormat'
import { initCashierMirrorPos } from '@/modules/cashier-mirror/cashierMirrorPosService'

const LOG_PREFIX = '[useAppBootstrap]'
const LOCAL_TICKETS_RETENTION_DAYS = 7

const isAppReady = ref(false)

const scan = ScanService()

export const useAppBootstrap = () => {
  const { showSpinner, closeSpinner } = useSpinnerStore()
  const settingsStore = useSettingsStore()
  const tokenPayloadStore = useTokenPayloadStore()
  const sevenIgamingPlatformConfig = useSevenIgamingPlatformConfig()
  const { operatorData, isOperatorLoggedIn, operatorDisplayName } = storeToRefs(
    useDmCoreSpaceOperatorStore(),
  )
  const { getOperatorData, startOperatorAccessTokenAutoRefresh } = useDmCoreSpaceOperatorAuthStore()
  const { appData } = storeToRefs(useDeviceManagementStore())
  const { getVoucherConfig, closeVoucherDetailsModal } = useVoucherPOSStore()
  const { closeSlipCheckDetailsModal } = useSlipCheckStore()
  const { initNotificationService, authenticateNotificationService } = useNotification()
  const { interfaceLanguage } = storeToRefs(settingsStore)
  const { syncStorageTicketsToStore } = useLatestBetslips(
    sevenIgamingPlatformConfig.slipsStorageDriver!,
    {
      retentionDays: LOCAL_TICKETS_RETENTION_DAYS,
    },
  )

  const startApp = async () => {
    showSpinner()

    try {
      const appRunUuid = appRunUuidStorage.getAppRunUuid()
      const printConfig: DmPrintConfig = {
        persist: { prefix: 'pos', storage: 'localStorage' },
      }

      if (!appRunUuid) {
        const errorMessage = 'appRunUuid is not defined.'
        const errorCode = 'SS_BOOT_APP_RUN_UUID_ERROR'

        logService.error(`${LOG_PREFIX} ${errorMessage}`, {
          code: errorCode,
        })

        throw new AppBootstrapError(errorMessage, errorCode)
      }

      sevenIgamingPlatformConfig.language = interfaceLanguage

      await deviceManagementService.initDeviceManagement({
        appName: 'Shop NxG',
        appRunUuid,
        autoSetDeviceAccessToken: isOperatorLoggedIn,
      })

      initCashierMirrorPos()

      if (!appData.value) {
        logService.error(`${LOG_PREFIX} App data is not defined`, {
          appRunUuid,
          code: 'SS_BOOT_APP_DATA_UNDEFINED',
        })
        return
      }

      registerPayoutHooks()

      await deviceManagementPrintService.init(printConfig).catch((error) => {
        const errorMessage = 'Failed to initialize DM print service.'
        const errorCode = 'SS_BOOT_DM_PRINT_SERVICE_INIT_ERROR'

        // app should continue to work even if print service is not initialized
        logService.error(`${LOG_PREFIX} ${errorMessage}`, {
          code: errorCode,
          ...errorParser.parseUpstream(error),
        })
      })

      if (import.meta.env.PROD) {
        httpLogPlugin.setAdditionalLogData({
          app_run_uuid: appRunUuid,
          tenant_id: appData.value.betshop.companyUuid,
          tenant_name: appData.value.betshop.companyName,
          device_id: appData.value.device.uuid,
          betshop_id: appData.value.betshop.uuid,
        })
      }

      settingsStore.getStoredSettings()
      const lang = settingsStore.resolveBootstrapLang(appData.value.appConfig.data.defaultLanguage)

      await cmsService
        .initCmsService('pos', {
          activeGames: appData.value!.appConfig.data.appConfig.products,
          tenantId: appData.value!.betshop.companyUuid,
          lang,
        })
        .then((cmsConfig) => {
          useCmsConfigStore().setCmsConfig(cmsConfig)
          useGameConfigurationStore().setGameConfigurations(cmsConfig.gameConfigurations.docs)
          useShortcutsStore().setShortcuts(cmsConfig.shortcuts.docs)
          applyTenantNumberFormat(cmsConfig.appSettings?.decimalFormat)
        })
        .catch((error) => {
          logService.error(`${LOG_PREFIX} Failed to initialize CMS service.`, {
            code: 'SS_BOOT_CMS_SERVICE_INIT_ERROR',
            ...errorParser.parseUpstream(error),
          })
          throw error
        })

      if (!operatorData.value && isOperatorLoggedIn.value) {
        logService.info(
          `${LOG_PREFIX} Operator is logged in but operator data is not available. Attempting to fetch operator data.`,
          {
            code: 'SS_BOOT_OPERATOR_DATA_FETCH_ATTEMPT',
          },
        )
        startOperatorAccessTokenAutoRefresh(() => {
          router.replace({ name: 'login' })
        })

        await getOperatorData().catch((error) => {
          logService.error(
            `${LOG_PREFIX} Failed to get operator data. Redirecting operator to login page`,
            {
              code: 'SS_BOOT_OPERATOR_DATA_FETCH_ERROR',
              ...errorParser.parseUpstream(error),
            },
          )
          router.replace({ name: 'login' })

          throw error
        })

        await setDeviceAccessToken().catch((error) => {
          logService.warn(`${LOG_PREFIX} Failed to set device access token.`, {
            code: 'SS_BOOT_SET_DEVICE_ACCESS_TOKEN_ERROR',
            ...errorParser.parseUpstream(error),
          })
        })

        await initTokenServices()
        bootGames()
        initPOSIntegrator()
      } else {
        bootGames()
      }

      settingsStore.loadLanguages()
      addScanListener()
      initSocketNotificationService()
      await syncStorageTicketsToStore()
      await cashRegisterService.getSessionsByTenant(appData.value?.betshop.companyUuid, {
        deviceUuid: appData.value?.device.uuid,
        limit: 10,
        offset: 0,
        sort: ['desc|openedAt'],
      })
    } catch (err) {
      logService.error(`${LOG_PREFIX} App bootstrap failed.`, {
        code: 'SS_BOOT_APP_ERROR',
        ...errorParser.parseUpstream(err),
      })
    } finally {
      if (isOperatorLoggedIn.value) {
        bindShortcutKeys()
        bindEmitKeydown()
      }

      isAppReady.value = true
      closeSpinner()
    }
  }

  const addScanListener = () => {
    scan.listenForScannerEvents((res) => {
      // close currently opened modal
      closeVoucherDetailsModal()
      closeSlipCheckDetailsModal()

      if (isVoucher(res.data.code)) {
        checkAndShowVoucherDetails(res.data.code)
      } else {
        checkAndShowSlipDetails(res.data.code)
      }
    })
  }

  const initTokenServices = async () => {
    try {
      printService.initPrintService()
    } catch (error) {
      logService.error(`${LOG_PREFIX} Failed to initialize client print service.`, {
        code: 'SS_BOOT_CLIENT_PRINT_SERVICE_INIT_ERROR',
        ...errorParser.parseUpstream(error),
      })
    }

    try {
      printService.addOperatorDataToGlobals(operatorDisplayName.value!)
    } catch (error) {
      logService.error(`${LOG_PREFIX} Failed to add operator data to print globals.`, {
        code: 'SS_BOOT_OPERATOR_PRINT_GLOBALS_ERROR',
        ...errorParser.parseUpstream(error),
      })
    }

    try {
      await getVoucherConfig()
    } catch (error) {
      logService.error(`${LOG_PREFIX} Failed to get voucher config.`, {
        code: 'SS_BOOT_VOUCHER_CONFIG_FETCH_ERROR',
        ...errorParser.parseUpstream(error),
      })
    }
  }

  const initSocketNotificationService = () => {
    const accessToken = getDeviceAccessToken()
    const isProduction = envService.isProductionEnv()
    const config: NotificationEventConfig = {
      tenantId: appData.value?.betshop.companyUuid || '',
      deviceId: appData.value?.device.uuid || '',
      env: isProduction ? 'production' : 'staging',
      debug: !isProduction,
      authToken: accessToken?.value,
      onConnectionIssue: (issue) => {
        logService.warn(`${LOG_PREFIX} Connection issue has occurred.`, {
          code: 'SS_NOTIFICATION_EVENT_CONNECTION_ISSUE',
          upstream_code: issue.type,
          upstream_message:
            issue.data instanceof CloseEvent
              ? `Websocket closed with code ${issue.data.code} and reason: ${issue.data.reason}`
              : 'Unknown connection issue',
          details: {
            wasClean: issue.data instanceof CloseEvent ? issue.data.wasClean : undefined,
          },
        })
      },
    }

    initNotificationService(config)

    watch(accessToken, (token) => {
      if (token) {
        authenticateNotificationService(token)
      }
    })
  }

  const handleDeviceAccessTokenReceived = (deviceAccessToken: string) => {
    try {
      tokenPayloadStore.setToken(deviceAccessToken)
      sevenIgamingPlatformConfig.token = deviceAccessToken
      configureRetailSdk({
        currencySymbol: getCurrencySymbol(tokenPayloadStore.baseCurrency || ''),
      })
    } catch (error) {
      logService.error(`${LOG_PREFIX} Failed to handle device access token.`, {
        code: 'SS_BOOT_DEVICE_ACCESS_TOKEN_HANDLE_ERROR',
        ...errorParser.parseUpstream(error),
      })
    }
  }

  const waitForAppReady = (): Promise<void> => {
    if (isAppReady.value) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      const stop = watch(isAppReady, (ready) => {
        if (ready) {
          stop()
          resolve()
        }
      })
    })
  }

  return {
    isAppReady: readonly(isAppReady),
    startApp,
    initTokenServices,
    handleDeviceAccessTokenReceived,
    waitForAppReady,
  }
}
